import { useState } from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import { ConfirmForgotPassword } from "./ConfirmForgotPassword";
import { usePasswordFieldToggle } from "../../../../hooks";
import { validateEmail } from "../../../../utils";
import { validatePassword } from "../utils";

import type { ConfirmForgotPasswordData } from "./types";
import type { ResetPasswordFormErrorState } from "../../types";

const validationFns: Record<string, (val: string) => string | null> = {
  email: validateEmail,
  new_password: validatePassword,
};

interface Props {
  email?: string;
  backToLogin: boolean;
  onSuccessFromAccountPage?: (success: boolean) => void;
}

export function ForgotPasswordContainer(props: Props) {
  const { email, backToLogin, onSuccessFromAccountPage = null } = props;
  const [error, setError] = useState<null | string>("");
  const [form, setForm] = useState<ConfirmForgotPasswordData>({
    email: email ?? "",
    code: "",
    new_password: "",
  });

  const [formError, setFormError] = useState<ResetPasswordFormErrorState>({
    email: null,
    new_password: null,
  });

  const { passwordIsVisible, handleShowPasswordChange } =
    usePasswordFieldToggle();

  const navigate = useNavigate();

  const handleFormChange = (
    key: keyof ConfirmForgotPasswordData,
    value: string,
  ) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (["email", "new_password"].includes(key)) {
      setFormError((prev) => ({
        ...prev,
        [key]: validationFns[key](value),
      }));
    }
  };

  const handleConfirmForgotPassword = () => {
    axios
      .post(
        `${import.meta.env.REACT_APP_API_URL}/confirm-forgot-password`,
        form,
      )
      .then(() => {
        setError(null);
        if (backToLogin) {
          navigate("/login");
        }
        if (onSuccessFromAccountPage != null) {
          onSuccessFromAccountPage(true);
        }
      })
      .catch((e) => {
        setError(
          e?.response?.data?.message ??
            "There was an error while resetting your password. Please contact us if the issue persists.",
        );
      });
  };

  return (
    <ConfirmForgotPassword
      onConfirmForgotPassword={handleConfirmForgotPassword}
      onFormChange={handleFormChange}
      formError={formError}
      disabledResetPassword={
        form.email.length === 0 ||
        form.code.length === 0 ||
        form.new_password.length === 0 ||
        formError.email !== null ||
        formError.new_password !== null
      }
      showPassword={passwordIsVisible}
      onShowPasswordChange={handleShowPasswordChange}
      error={error}
      email={email}
      backToLogin={backToLogin}
    />
  );
}
